import { Alerts } from 'constants/alerts';

import React, { useEffect, useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { useListContext } from 'react-admin';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { FinanceUserAlert } from 'shared/mui/Alert/FinanceUserAlert/FinanceUserAlert';
import { SuccessReportConvertedAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportConvertedAlert';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { CompanyBalanceReport } from 'types/embedded/balanceReport';
import { formatInputToTimestamp } from 'utils/date';

import { BalanceReportFilterValues } from '../BalanceReportList';
import { BalanceReportDatagrid } from './BalanceReportDatagrid/BalanceReportDatagrid';
import { BoxStyled } from './BalanceReportListContent.styled';
import { BalanceReportContextProvider } from '../context/BalanceReportContext';

type Props = {
  filters: BalanceReportFilterValues;
  isLoading: boolean;
  toggleDrawer: (open: boolean) => () => void;
};

export const BalanceReportListContent: React.FC<Props> = ({ filters, isLoading, toggleDrawer }) => {
  const { data } = useListContext<CompanyBalanceReport>();
  const [showSuccessAlert, setShowSuccessAlert] = useState(true);

  const { user } = useApiContext();

  useEffect(() => {
    setShowSuccessAlert(true);
  }, [data]);

  if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
    return <FinanceUserAlert firstName={user.firstName} lastName={user.lastName} />;
  }

  if (isLoading) {
    return (
      <BoxStyled border="1px solid" borderColor="divider" borderRadius={1}>
        <DatagridSkeleton columnsCount={16} />
      </BoxStyled>
    );
  }

  if (!data?.length) {
    return <EmptyListAlert onClick={toggleDrawer(true)} />;
  }

  return (
    <>
      {!!data?.length && showSuccessAlert && (
        <SuccessReportConvertedAlert
          conversionRateType={filters?.conversionRateType}
          currency={filters.currency || ''}
          date={{
            gte: formatInputToTimestamp(filters?.appliedDate?.[0], { unit: 'seconds' }),
            lte: formatInputToTimestamp(filters?.appliedDate?.[1], { unit: 'seconds' }),
          }}
          onClose={() => setShowSuccessAlert(false)}
          timezone={filters?.timezone || DEFAULT_TIMEZONE}
        />
      )}
      {!!data?.length && (
        <BalanceReportContextProvider value={{ filters }}>
          <BoxStyled border="1px solid" borderColor="divider" borderRadius={1}>
            <BalanceReportDatagrid data={data} />
          </BoxStyled>
        </BalanceReportContextProvider>
      )}
    </>
  );
};
